import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function generateTask() {
  const x = getRandomInt(3, 15);
  const rAbs = getRandomInt(1, 10);

  // Losujemy kierunek, żeby jednoznacznie wybrać znak r
  const direction = Math.random() < 0.5 ? "rosnacy" : "malejacy";
  const r = direction === "rosnacy" ? rAbs : -rAbs;

  // Trzy kolejne wyrazy w kolejności ciągu (a1, a2, a3)
  // Dla rosnącego: (x-r, x, x+r)
  // Dla malejącego: (x-r) > x > (x+r) bo r<0, więc to nadal są kolejne wyrazy
  const a1 = x - r;
  const a2 = x;
  const a3 = x + r;

  const sum = 3 * x;
  const squareSum = 2 * x * x + 2 * rAbs * rAbs; // zależy od r^2, więc od |r|

  return {
    type: "Find3TermArithmeticTask",
    data: { sum, squareSum, direction },
    answer: [a1, a2, a3]
  };
}

export function renderTask({ data }) {
  const { sum, squareSum, direction } = data;

  const directionText = direction === "rosnacy" ? "rosnący" : "malejący";

  return (
    <div className="space-y-2">
      <p>
        Trzy liczby tworzą ciąg arytmetyczny<InlineMath math=" (a, b, c)" />{" "}
        <b>{directionText}</b>.
      </p>
      <p>
        Ich suma wynosi <InlineMath math={`${sum}`} />, a suma kwadratów liczb
        skrajnych wynosi <InlineMath math={`${squareSum}`} />.
      </p>
      <p>Wyznacz te liczby.</p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  const getVal = (index) => userAnswer?.[index] || "";

  const update = (index, newVal) => {
    const copy = [...(userAnswer || [])];
    copy[index] = newVal;
    onChange(copy);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math="a =" />
        <FractionInput
          value={getVal(0)}
          onChange={(val) => update(0, val)}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math="b =" />
        <FractionInput
          value={getVal(1)}
          onChange={(val) => update(1, val)}
          disabled={disabled}
          label="b"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math="c =" />
        <FractionInput
          value={getVal(2)}
          onChange={(val) => update(2, val)}
          disabled={disabled}
          label="c"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!Array.isArray(userAnswer)) return <i>(brak odpowiedzi)</i>;

  const [a, b, c] = userAnswer.map((val) =>
    formatLatexPreview(val?.raw ?? "", "")
  );

  return <InlineMath math={`a = ${a},\\quad b = ${b},\\quad c = ${c}`} />;
}

export function renderAnswer({ answer }) {
  const [a, b, c] = answer;
  return <InlineMath math={`a = ${a},\\quad b = ${b},\\quad c = ${c}`} />;
}

export function checkAnswer({ answer }, userAnswer) {
  if (!Array.isArray(userAnswer) || userAnswer.length !== 3) return false;

  const parsed = userAnswer.map((val) => val?.parsed);
  if (parsed.some((x) => x == null || isNaN(x))) return false;

  return parsed.every((val, i) => Math.abs(val - answer[i]) < 0.01);
}

export function renderExplanation({ data, answer }) {
  const { sum, squareSum, direction } = data;
  const [a, b, c] = answer;

  const x = b; // środkowy wyraz
  const x2 = x * x;

  const directionText = direction === "rosnacy" ? "rosnący" : "malejący";
  const signText =
    direction === "rosnacy"
      ? "ponieważ ciąg jest rosnący, to r > 0"
      : "ponieważ ciąg jest malejący, to r < 0";

  const r2 = (squareSum - 2 * x2) / 2;
  const rAbs = Math.sqrt(r2);
  const r = direction === "rosnacy" ? rAbs : -rAbs;

  return (
    <div className="space-y-4">
      <p>
        <b>Krok 1:</b> Skoro to trzy kolejne wyrazy ciągu arytmetycznego, możemy
        je zapisać jako:
      </p>
      <BlockMath math={"x - r,\\ x,\\ x + r"} />
      <p>
        (ciąg jest <b>{directionText}</b>)
      </p>

      <p>
        <b>Krok 2:</b> Z warunku sumy:
      </p>
      <BlockMath math={`(x - r) + x + (x + r) = 3x = ${sum}`} />
      <BlockMath math={`x = \\frac{${sum}}{3} = ${x}`} />

      <p>
        <b>Krok 3:</b> Z warunku sumy kwadratów liczb skrajnych:
      </p>
      <BlockMath math={`(x - r)^2 + (x + r)^2 = ${squareSum}`} />
      <p>Po redukcji otrzymujemy:</p>
      <BlockMath math={"(x - r)^2 + (x + r)^2 = 2x^2 + 2r^2"} />
      <p>Podstawiamy <InlineMath math={`x = ${x}`} />:</p>
      <BlockMath math={`2\\cdot ${x2} + 2r^2 = ${squareSum}`} />
      <BlockMath math={`2r^2 = ${squareSum} - ${2 * x2} = ${squareSum - 2 * x2}`} />
      <BlockMath math={`r^2 = ${r2}`} />

      <p>
        <b>Krok 4:</b> Z równania <InlineMath math={"r^2"} /> wynikają dwa znaki,
        ale w zadaniu mamy warunek: {signText}.
      </p>
      <BlockMath math={`r = ${r}`} />

      <p>
        <b>Krok 5:</b> Obliczamy liczby:
      </p>
      <BlockMath math={`x - r = ${a},\\quad x = ${b},\\quad x + r = ${c}`} />
    </div>
  );
}
